/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { FAQItem } from "./faq.utils";

/**
 * Upper bound on the HTML we regex-process per field. Keeps the tag-scanning
 * passes bounded (a pathological unclosed-tag blob can't wedge an SSR thread) and
 * stops the emitted `<script>` from ballooning. Real FAQ answers are a few KB; only
 * the JSON-LD copy is capped — the visible accordion content is rendered separately
 * by the section, uncapped.
 *
 * Security-relevant: the `<script>`/`<style>`/`<iframe>` body-removal regexes are
 * O(n²) in the worst case, so this cap is what pins their runtime (~120ms at 50k).
 * Do NOT raise it materially (e.g. into the hundreds of KB) without first making
 * those regexes linear — doing so would reopen a ReDoS/SSR-stall vector.
 */
const MAX_HTML_LENGTH = 50_000;

const capHtml = (html: string): string => (html.length > MAX_HTML_LENGTH ? html.slice(0, MAX_HTML_LENGTH) : html);

/**
 * Named HTML entities we decode when flattening CMS rich text to the plain-text
 * `name` of a FAQ question (and feed titles/descriptions). Kept intentionally small —
 * the common set produced by the CKEditor/Hygraph pipeline and forum excerpts.
 * Numeric entities are handled separately.
 */
const NAMED_ENTITIES: Record<string, string> = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: '"',
	apos: "'",
	nbsp: " ",
	hellip: "\u2026",
	mdash: "\u2014",
	ndash: "\u2013",
	lsquo: "\u2018",
	rsquo: "\u2019",
	ldquo: "\u201c",
	rdquo: "\u201d",
};

/**
 * Decodes named (`&amp;`) and numeric (`&#160;` / `&#xA0;`) HTML entities to their
 * literal characters. Unknown entities are left untouched.
 */
const decodeHtmlEntities = (text: string): string =>
	text.replace(/&(#[xX]?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (match, entity: string): string => {
		if (entity[0] === "#") {
			const isHex = entity[1] === "x" || entity[1] === "X";
			const code = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);
			if (Number.isNaN(code) || code < 0 || code > 0x10ffff) return match;
			try {
				return String.fromCodePoint(code);
			} catch {
				return match;
			}
		}
		const decoded = NAMED_ENTITIES[entity.toLowerCase()];
		return decoded !== undefined ? decoded : match;
	});

/**
 * Flattens CMS rich-text HTML to a single line of plain text. Used for a FAQ
 * question's `name`, which schema.org / Google require to be plain text.
 *
 * Drops `<script>`/`<style>` bodies, strips remaining tags (replacing them with a
 * space so adjacent words don't fuse), decodes HTML entities, then collapses
 * whitespace.
 *
 * Deliberately NOT `getPlainText` (CosmosText.atom.tsx): that helper *drops* most
 * entities (`&amp;` → ``), which would silently corrupt the JSON-LD `name`. Here we
 * *decode* them (`&amp;` → `&`) so the structured data matches the visible text.
 * The bounded `[^<>]*` tag classes (vs `[^>]*`) keep an unterminated `<` from
 * scanning to end-of-input — the same linear-time idiom used in CosmosRichText.utils.
 */
export const stripHtmlToText = (html?: string | null): string => {
	if (!html) return "";

	const withoutTags = capHtml(html)
		.replace(/<(script|style)\b[^<>]*>[\s\S]*?<\/\1>/gi, " ")
		.replace(/<!--[\s\S]*?-->/g, " ")
		.replace(/<[^<>]*>/g, " ");

	return decodeHtmlEntities(withoutTags).replace(/\s+/g, " ").trim();
};

/**
 * Tags Google permits inside a FAQ `acceptedAnswer.text`. Anything outside this
 * set is stripped (inner text preserved).
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */
const ALLOWED_ANSWER_TAGS = new Set([
	"a",
	"p",
	"br",
	"b",
	"strong",
	"i",
	"em",
	"ul",
	"ol",
	"li",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"div",
]);

/**
 * Allows relative URLs and the http/https/mailto/tel schemes; rejects any other
 * scheme (`javascript:`, `data:`, etc.) on FAQ answer links.
 */
const isSafeHref = (href: string): boolean => {
	const trimmed = href.trim();
	if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return true;
	// A leading scheme we didn't allow -> reject; otherwise treat as a relative URL.
	return !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
};

/**
 * Sanitizes CMS answer HTML down to the tag allowlist Google supports for
 * `acceptedAnswer.text`. Removes `<script>`/`<style>`/`<iframe>` (with their
 * content) and HTML comments, strips disallowed tags while keeping their inner
 * text, and drops every attribute except a safe `href` on `<a>`.
 *
 * Note: this is a best-effort SEO-cleanliness pass, not the XSS boundary — the
 * `<`/`>`/`&` escaping in {@link buildFaqPageJsonLd} is what prevents a
 * `</script>` breakout from the emitted `<script>` element.
 */
export const sanitizeAnswerHtml = (html?: string | null): string => {
	if (!html) return "";

	let out = capHtml(html)
		.replace(/<(script|style|iframe)\b[^<>]*>[\s\S]*?<\/\1>/gi, "")
		.replace(/<\/?(script|style|iframe)\b[^<>]*>/gi, "")
		.replace(/<!--[\s\S]*?-->/g, "");

	out = out.replace(
		/<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^<>]*)>/g,
		(_match, slash: string, rawName: string, attrs: string): string => {
			const name = rawName.toLowerCase();
			if (!ALLOWED_ANSWER_TAGS.has(name)) return ""; // strip disallowed tag, keep inner text
			if (slash === "/") return `</${name}>`;

			if (name === "a") {
				const hrefMatch = attrs.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/i);
				const rawHref = hrefMatch ? (hrefMatch[2] ?? hrefMatch[3] ?? hrefMatch[4] ?? "") : "";
				const href = rawHref.replace(/["<>]/g, "").trim();
				return href && isSafeHref(href) ? `<a href="${href}">` : "<a>";
			}

			return `<${name}>`; // opening allowed tag, attributes stripped
		},
	);

	return out.replace(/\s+/g, " ").trim();
};

/**
 * Builds a schema.org `FAQPage` JSON-LD string from FAQ items, ready to embed in a
 * `<script type="application/ld+json">` element.
 *
 * - `name` is the question flattened to plain text ({@link stripHtmlToText}).
 * - `acceptedAnswer.text` is the answer with only Google-supported HTML
 *   ({@link sanitizeAnswerHtml}). The empty-item gate is keyed on the *plain-text*
 *   form of each field (so a markup-only, text-less answer is dropped), while the
 *   value actually emitted keeps the sanitized HTML.
 * - Items whose question or answer has no visible text are dropped; `null` is
 *   returned when nothing valid remains (so callers can skip rendering).
 *
 * The serialized JSON is escaped for safe embedding inside an HTML `<script>`:
 * `<`, `>` and `&` are replaced with their `\uXXXX` escapes so no `</script>` (or
 * comment/CDATA) sequence can terminate the element. This is lossless — any JSON-LD
 * parser reverses it, so answer markup such as `<a href>`/`<ul>` survives. (Raw
 * U+2028/U+2029, which `JSON.stringify` leaves intact, are already normalized to
 * spaces by the strip/sanitize passes, so they never reach this string.)
 */
export const buildFaqPageJsonLd = (items: FAQItem[] = []): string | null => {
	const mainEntity = (items || [])
		.map((item) => ({
			name: stripHtmlToText(item?.question?.html),
			answerText: stripHtmlToText(item?.answer?.html),
			answerHtml: sanitizeAnswerHtml(item?.answer?.html),
		}))
		.filter(({ name, answerText }) => name.length > 0 && answerText.length > 0)
		.map(({ name, answerHtml }) => ({
			"@type": "Question",
			name,
			acceptedAnswer: {
				"@type": "Answer",
				text: answerHtml,
			},
		}));

	if (mainEntity.length === 0) return null;

	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity,
	};

	return JSON.stringify(jsonLd).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
};
